// ── „Heute“ — Kopfzeile, Hinweis-Leiste, Widget-Board ──
// D3b: Board (Layout je Lehrkraft und Gerätetyp), D3c: Widget-Inhalte in features/heute/widgets
// (jede Zeile/Zahl führt an ihr Ziel; „nicht geladen“ ≠ „leer“).
import React, { useState, useEffect } from 'react';
import { MIcon } from '../../lib/micon';
import { useIsMobile } from '../../lib/hooks';
import { API_BASE, apiHeaders, apiPost, leseFallback, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { getAktuellerNutzer } from '../../lib/aktuellerNutzer';
import { nimmNoteVorschlaegeZeigen } from '../../lib/nav';
import { LdSheet } from '../../components/ui/LdSheet';
import { Button } from '../../components/ld/Button';
import { useRechtliches, VerarbeitungsListe } from '../rechtliches/RechtsTexte';
import { useBoard } from './board/useBoard';
import { BoardPc } from './board/BoardPc';
import { BoardHandy } from './board/BoardHandy';
import type { WidgetInhalt } from './board/WidgetInhalt';
import './board/board.css';
import './widgets/widgets.css';
import './board/board-pc.css';
import { BOARD_ANPASSEN_KEY } from './board/anpassen';
import { DeinTagWidget, datumsZeile, useDeinTag } from './widgets/DeinTag';
import {
  DiktatWidget, LerngruppenWidget, MailsWidget, MeinTagWidget, MeinTagZusatz, RadarWidget, TermineWidget, WocheWidget, WocheZusatz,
} from './widgets/Uebersicht';
import {
  AktionenWidget, AktivitaetWidget, AusfluegeWidget, ElternZuordnungWidget, KlassenarbeitenWidget, KlassenbuchOffenWidget,
  KlassenbuchOffenZusatz, MusterWidget, NotizenWidget, StundenplanWocheWidget,
} from './widgets/Weitere';
import { Hinweis, StandardHinweise } from './widgets/Hinweise';
import { NoteVorschlaegeFenster, noteVorschlagText, kwText } from './widgets/NoteVorschlaege';
import { LdAuswahl } from '../../components/ld/LdFenster';

// Welle D2 (Nebenbefund-Fix): Key = Montag der Woche
function dsgvoWeekKey(): string {
  const now = new Date();
  const montag = new Date(now);
  montag.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return montag.toISOString().slice(0, 10);
}

// Welle D3c: bis 11 Uhr Morgen, bis 17 Uhr Tag, danach Abend
function begruessung(stunde: number): string {
  if (stunde < 11) return 'Guten Morgen';
  if (stunde < 17) return 'Guten Tag';
  return 'Guten Abend';
}

const NOTE6_ZU_KEY = 'ld-note6-hinweis-zu';

export type NoteVorschlagDash = {
  id: number; schueler_id: number; fach: string; schul_woche: string;
  vorgeschlagene_note: number; vorfaelle_anzahl: number; datums: string[];
  vorname: string; nachname: string;
};

export function HeuteScreen() {
  const nutzer = getAktuellerNutzer();
  const isMobile = useIsMobile();
  // ── Board + Anpassen-Modus (D3b) ──
  const board = useBoard();
  const [bearbeiten, setBearbeitenRoh] = useState(() => {
    try { return sessionStorage.getItem(BOARD_ANPASSEN_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    if (bearbeiten && board.daten) { try { sessionStorage.removeItem(BOARD_ANPASSEN_KEY); } catch { /* egal */ } }
  }, [bearbeiten, board.daten]);
  const setBearbeiten = React.useCallback((b: boolean) => {
    setBearbeitenRoh(b);
    if (b) window.scrollTo({ top: 0 });
  }, []);

  // ── „Dein Tag“: EIN Lader für Kopfzeile und Widget ──
  const deinTag = useDeinTag();
  const anzeigeName = (deinTag.daten?.vorname || nutzer?.vorname || '').trim() || null;

  // ── Note-6-Rückfragen (Pflicht-Entscheidung → Hinweis über dem Board) ──
  const [noteVorschlaege, setNoteVorschlaege] = useState<NoteVorschlagDash[]>([]);
  const [dashVorschlagNoten, setDashVorschlagNoten] = useState<Record<number, number>>({});
  const [alleNote6, setAlleNote6] = useState(false);
  function ladeDashVorschlaege() {
    fetch(`${API_BASE}/api/mdl-vorschlaege`, { headers: apiHeaders() })
      .then(leseFallback<NoteVorschlagDash[]>([], '/api/mdl-vorschlaege'))
      .then((data: NoteVorschlagDash[]) => {
        setNoteVorschlaege(data);
        const init: Record<number, number> = {};
        data.forEach(v => { init[v.id] = v.vorgeschlagene_note; });
        setDashVorschlagNoten(init);
      })
      .catch(() => {});
  }
  useEffect(() => { ladeDashVorschlaege(); }, []);
  // Welle B1: Push „Offene Note-Vorschläge“ → Liste ausgeklappt und im Blick
  const note6Ref = React.useRef<HTMLDivElement>(null);
  const [note6Zeigen, setNote6Zeigen] = useState(false);
  // Welle K6: am PC eine ruhige Hinweis-Zeile, „Prüfen“ öffnet die Liste im Fenster
  const [note6Fenster, setNote6Fenster] = useState(false);
  const [note6Zu, setNote6Zu] = useState(() => { try { return sessionStorage.getItem(NOTE6_ZU_KEY) ?? ''; } catch { return ''; } });
  useEffect(() => { if (nimmNoteVorschlaegeZeigen()) { setNote6Zeigen(true); setAlleNote6(true); } }, []);
  useEffect(() => {
    if (!note6Zeigen || noteVorschlaege.length === 0) return;
    if (isMobile) note6Ref.current?.scrollIntoView({ block: 'start' });
    else { setNote6Fenster(true); setNote6Zeigen(false); }
  }, [note6Zeigen, noteVorschlaege.length, isMobile]);
  const note6Schluessel = noteVorschlaege.map(v => v.id).join(',');
  function note6Ausblenden() {
    setNote6Zu(note6Schluessel);
    try { sessionStorage.setItem(NOTE6_ZU_KEY, note6Schluessel); } catch { /* egal */ }
  }
  async function note6Bestaetigen(v: NoteVorschlagDash) {
    try {
      await apiPost(`/api/mdl-vorschlaege/${v.id}/bestaetigen`, { bestaetigte_note: dashVorschlagNoten[v.id] ?? v.vorgeschlagene_note });
      toast('Note eingetragen'); ladeDashVorschlaege();
    } catch (e) { zeigeApiFehler(e, 'Fehler beim Bestätigen'); }
  }
  async function note6Verwerfen(v: NoteVorschlagDash) {
    try { await apiPost(`/api/mdl-vorschlaege/${v.id}/verwerfen`); ladeDashVorschlaege(); }
    catch (e) { zeigeApiFehler(e, 'Fehler beim Verwerfen'); }
  }

  // ── Datenschutz: nur bei neuer Fassung (U10) ──
  const [dsgvoOffen, setDsgvoOffen] = useState(false);
  const [dsgvoFensterOffen, setDsgvoFensterOffen] = useState(false);
  const [dsgvoSpeichert, setDsgvoSpeichert] = useState(false);
  const DSGVO_BANNER_KEY = 'ld-dsgvo-banner-dismissed';
  useEffect(() => {
    const dismissed = localStorage.getItem(DSGVO_BANNER_KEY);
    fetch(`${API_BASE}/api/einwilligung/status`, { headers: apiHeaders() })
      .then(leseFallback<any>(null, '/api/einwilligung/status'))
      .then(data => { if (data?.needs_update && dismissed !== dsgvoWeekKey()) setDsgvoOffen(true); })
      .catch(() => {});
  }, []);
  async function dsgvoFassungBestaetigen() {
    setDsgvoSpeichert(true);
    try {
      // KI-Einwilligung unverändert mitsenden: frisch vom Server, nie ein Standardwert
      const status = await fetch(`${API_BASE}/api/einwilligung/status`, { headers: apiHeaders() })
        .then(leseFallback<any>(null, '/api/einwilligung/status'));
      if (!status) throw new Error('Status nicht lesbar');
      await apiPost('/api/einwilligung/aktualisieren', { ki_verarbeitung: status.ki_verarbeitung_akzeptiert === true });
      setDsgvoFensterOffen(false);
      setDsgvoOffen(false);
      toast('Aktuelle Fassung bestätigt');
    } catch (e) { zeigeApiFehler(e, 'Bestätigen fehlgeschlagen'); } finally { setDsgvoSpeichert(false); }
  }
  function dsgvoSpaeter() {
    localStorage.setItem(DSGVO_BANNER_KEY, dsgvoWeekKey());
    setDsgvoFensterOffen(false);
    setDsgvoOffen(false);
  }

  // ── PWA-Installation (Chrome/Android) im Seitenfluss (D3b) ──
  const [installPrompt, setInstallPrompt] = useState<Event | null>(null);
  const [installDismissed, setInstallDismissed] = useState(() => localStorage.getItem('ld-install-dismissed') === '1');
  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  function handleInstall() {
    if (!installPrompt) return;
    (installPrompt as any).prompt?.();
    setInstallPrompt(null);
  }

  const heuteIso = new Date().toLocaleDateString('sv-SE');
  const gruss = begruessung(new Date().getHours());
  const elKopf = (
    <header className="ld-heute-kopf">
      <div className="ld-heute-kopf__text">
        <h1 className="ld-heute-kopf__gruss">{anzeigeName ? `${gruss}, ${anzeigeName}.` : `${gruss}.`}</h1>
        <p className="ld-heute-kopf__zeile">{datumsZeile(deinTag.daten, heuteIso)}</p>
      </div>
      {!bearbeiten && board.daten && (
        <button type="button" className="ld-heute-kopf__anpassen" onClick={() => setBearbeiten(true)}>
          <MIcon name="dashboard_customize" size={18} />
          <span className="ld-heute-kopf__anpassen-text">Anpassen</span>
        </button>
      )}
    </header>
  );

  const elNote6Pc = noteVorschlaege.length > 0 && note6Zu !== note6Schluessel && (
    <Hinweis icon="warning" ton="amber"
      titel={`${noteVorschlaege.length} ${noteVorschlaege.length === 1 ? 'Note-Vorschlag' : 'Note-Vorschläge'}`}
      text={noteVorschlagText(noteVorschlaege)}
      aktion={{ label: 'Prüfen', onClick: () => setNote6Fenster(true) }}
      schliessenLabel="Hinweis für diese Sitzung ausblenden"
      onSchliessen={note6Ausblenden} />
  );

  const elNote6 = noteVorschlaege.length > 0 && (
    <div ref={note6Ref} className="ld-hinweis ld-hinweis--amber ld-hinweis--liste" style={{ scrollMarginTop: 88 }}>
      <div className="ld-hinweis__zeile">
        <span className="ld-hinweis__icon"><MIcon name="grading" size={18} /></span>
        <div className="ld-hinweis__text"><b>Offene Note-Vorschläge ({noteVorschlaege.length})</b><span>Vorschläge aus unentschuldigten Fehlzeiten: Note bestätigen oder verwerfen.</span></div>
      </div>
      {(alleNote6 ? noteVorschlaege : noteVorschlaege.slice(0, 2)).map(v => (
        <div key={v.id} className="ld-note6">
          <div className="ld-note6__wer">
            <b>{v.vorname} {v.nachname}</b>
            <span>{v.fach} · {kwText(v.schul_woche)} · {v.vorfaelle_anzahl}× unentsch.</span>
          </div>
          <LdAuswahl className="ld-note6__note" style={{ width: 76, minHeight: 40 }} aria-label={`Note für ${v.vorname} ${v.nachname}`} value={dashVorschlagNoten[v.id] ?? v.vorgeschlagene_note} onChange={e => setDashVorschlagNoten(prev => ({ ...prev, [v.id]: Number(e.target.value) }))}>
            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
          </LdAuswahl>
          <button type="button" className="ld-hinweis__knopf" onClick={() => note6Bestaetigen(v)}>Bestätigen</button>
          <button type="button" className="ld-hinweis__neben" onClick={() => note6Verwerfen(v)}>Verwerfen</button>
        </div>
      ))}
      {noteVorschlaege.length > 2 && (
        <button type="button" className="ld-note6-mehr" onClick={() => setAlleNote6(a => !a)}>
          {alleNote6 ? 'Weniger anzeigen' : `Alle ${noteVorschlaege.length} anzeigen`}
        </button>
      )}
    </div>
  );

  const elHinweise = (
    <div className="ld-heute-hinweise">
      {dsgvoOffen && (
        <Hinweis icon="shield" ton="amber" titel="Datenschutz-Hinweise aktualisiert"
          text="Bitte die neue Fassung ansehen und bestätigen."
          aktion={{ label: 'Ansehen & bestätigen', onClick: () => setDsgvoFensterOffen(true) }}
          neben={{ label: 'Später', onClick: dsgvoSpaeter }} />
      )}
      <StandardHinweise />
      {isMobile ? elNote6 : elNote6Pc}
      {installPrompt && !installDismissed && (
        <Hinweis icon="install_mobile" titel="LehrerDesk als App installieren" text="Schnellzugriff vom Homescreen"
          aktion={{ label: 'Installieren', onClick: handleInstall }}
          schliessenLabel="Schließen"
          onSchliessen={() => { setInstallDismissed(true); localStorage.setItem('ld-install-dismissed', '1'); }} />
      )}
    </div>
  );

  const inhalte: Record<string, WidgetInhalt> = {
    'dein-tag':    { body: <DeinTagWidget g={deinTag} /> },
    'radar':       { body: <RadarWidget /> },
    'lerngruppen': { body: <LerngruppenWidget /> },
    'mails':       { body: <MailsWidget /> },
    'diktat':      { body: <DiktatWidget /> },
    'woche':       { body: <WocheWidget />, zusatz: <WocheZusatz /> },
    'termine':     { body: <TermineWidget /> },
    'mein-tag':    { body: <MeinTagWidget />, zusatz: <MeinTagZusatz /> },
    'ausfluege':   { body: <AusfluegeWidget /> },
    'notizen-angepinnt': { body: <NotizenWidget /> },
    'stundenplan-woche': { body: <StundenplanWocheWidget /> },
    'klassenbuch-offen': { body: <KlassenbuchOffenWidget />, zusatz: <KlassenbuchOffenZusatz /> },
    'klassenarbeiten':   { body: <KlassenarbeitenWidget /> },
    'eltern-zuordnung':  { body: <ElternZuordnungWidget /> },
    'klassenbuch-muster':     { body: <MusterWidget /> },
    'klassenbuch-aktivitaet': { body: <AktivitaetWidget /> },
    'aktionen':          { body: <AktionenWidget /> },
  };

  const d = board.daten;
  return (
    <>
    <div className="ld-heute" style={{
      padding: isMobile ? '0 var(--ld-space-base,16px) 96px' : '0 var(--ld-space-2xl,32px) 80px',
    }}>
      {!bearbeiten && elKopf}
      {!bearbeiten && elHinweise}
      {!d ? (
        board.ladeFehler ? (
          <div className="ld-board__leer">
            <MIcon name="cloud_off" size={30} />
            <p>Dein Board konnte nicht geladen werden. Prüf die Verbindung und lade die Seite neu.</p>
          </div>
        ) : (
          <div className="ld-board__laden" aria-busy="true" aria-label="Board wird geladen">
            <div className="ld-skeleton" /><div className="ld-skeleton" /><div className="ld-skeleton" />
          </div>
        )
      ) : isMobile ? (
        <BoardHandy
          layout={d.mobil.layout}
          defs={board.defs}
          inhalte={inhalte}
          bearbeiten={bearbeiten}
          setBearbeiten={setBearbeiten}
          speichere={(l, sofort) => board.speichere('mobil', l, sofort)}
          zuruecksetzen={() => board.zuruecksetzen('mobil')}
        />
      ) : (
        <BoardPc
          layout={d.pc.layout}
          defs={board.defs}
          inhalte={inhalte}
          bearbeiten={bearbeiten}
          setBearbeiten={setBearbeiten}
          speichere={(l, sofort) => board.speichere('pc', l, sofort)}
          zuruecksetzen={() => board.zuruecksetzen('pc')}
        />
      )}
    </div>

    <NoteVorschlaegeFenster open={note6Fenster && !isMobile} onClose={() => setNote6Fenster(false)}
      vorschlaege={noteVorschlaege} noten={dashVorschlagNoten}
      setNote={(id, n) => setDashVorschlagNoten(prev => ({ ...prev, [id]: n }))}
      onBestaetigen={note6Bestaetigen} onVerwerfen={note6Verwerfen} />

    <LdSheet open={dsgvoFensterOffen} onClose={() => setDsgvoFensterOffen(false)} maxWidth={580} title="Aktualisierte Datenschutz-Hinweise">
      <DsgvoUebersicht />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
        <Button variant="primary" block disabled={dsgvoSpeichert} onClick={dsgvoFassungBestaetigen}>Aktuelle Fassung bestätigen</Button>
        <Button variant="ghost" block disabled={dsgvoSpeichert} onClick={dsgvoSpaeter}>Später</Button>
      </div>
    </LdSheet>
    </>
  );
}

// Welle U10: Verarbeitungsübersicht im Bestätigen-Fenster — dieselbe Liste wie in
// der Datenschutzerklärung (öffentlich geladen, server/lib/rechtliches.js).
function DsgvoUebersicht() {
  const { daten, fehler } = useRechtliches();
  if (!daten) return <p style={{ margin: 0, fontSize: 13, color: 'var(--ld-text-secondary)' }}>{fehler ? 'Die Übersicht konnte nicht geladen werden.' : 'Lädt …'}</p>;
  const d = daten.datenschutzerklaerung;
  return (
    <div>
      <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--ld-text-secondary)' }}>
        Fassung {d.fassung} · Stand {d.stand}. So verarbeitet LehrerDesk deine Daten:{' '}
        <a href="/datenschutzerklaerung" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ld-accent)', fontWeight: 600 }}>ganze Datenschutzerklärung lesen</a>
      </p>
      <VerarbeitungsListe verarbeitungen={d.verarbeitungen} />
    </div>
  );
}
